import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LogoutService } from 'app/core/auth/logout.service';
import { AuthService } from 'app/core/auth/auth.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-layouts',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './layouts.component.html',
  styleUrl: './layouts.component.scss'
})
export class LayoutsComponent {
    isDropdownOpen = false;
    isLoggingOut = false;
    isSubMenuOpen: boolean = false;
    activeMenu: string = '';
    isMobileSidebarOpen: boolean = false;
    isSidebarMinimized: boolean = false;
    isSettingDropdownOpen: boolean = false;

    // User data properties
    userData: any = null;
    userName: string = 'Guest';
    userEmail: string = '';
    userRole: string = '';
    userAvatar: string = 'https://i.pravatar.cc/100?img=5';

    constructor(
        private logoutService: LogoutService,
        private router: Router,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        const savedMinimized = localStorage.getItem('isSidebarMinimized');
        this.isSidebarMinimized = savedMinimized === 'true';

        // Get user data from AuthService
        this.loadUserData();

        // Subscribe to user data changes
        this.authService.userData$.subscribe(userData => {
            if (userData) {
                this.userData = userData;
                this.userName = userData.username || 'Guest';
                this.userEmail = userData.email || '';
                this.userRole = userData.role || 'user';
                this.userAvatar = userData.avatar || 'https://i.pravatar.cc/100?img=5';
                // console.log('User data updated in layout:', userData);
            }
        });
    }

    loadUserData(): void {
        this.userData = this.authService.getCurrentUser();
        // console.log('User Data:', this.userData);

        if (this.userData) {
            this.userName = this.userData.username || 'Guest';
            this.userEmail = this.userData.email || '';
            this.userRole = this.userData.role || 'user';
            this.userAvatar = this.userData.avatar || 'https://i.pravatar.cc/100?img=5';
        }
    }

    // Helper method to check if user is tenant
    isTenant(): boolean {
        return this.userData?.is_tenant === true;
    }

    // Helper method to check if user is admin
    isAdmin(): boolean {
        return this.userData?.is_tenant === false;
    }

    // Helper method to check if user can access tenant-specific menus (Product, Category, Order, Payment)
    canAccessTenantManagement(): boolean {
        // Only tenant users (is_tenant: true) can access these menus
        return this.isTenant();
    }

    // Helper method to check if user can access Tenant management menu
    canAccessAdminManagement(): boolean {
        // Only admin users (is_admin: true) can access Tenant menu
        return this.isAdmin();
    }

    goToDashboard() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'dashboard';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/dashboard']);
    }

    goToUser() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'user';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/user']);
    }

    goToTenant() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'tenant';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/tenant']);
    }

    goToRole() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'role';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/role']);
    }

    goToPermission() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'permission';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/permission']);
    }

    goToProduct() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'product';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/product']);
    }

    goToCategory() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'category';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/category']);
    }

    goToOrder() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'order';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/order']);
    }

    goToPaymentGateway() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'payment-gateway';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/payment-gateway']);
    }

    goToPaymentMethod() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'payment-method';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/payment-method']);
    }

    goToPaymentBank() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'payment-bank';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/payment-bank']);
    }

    goToPaymentChannel() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'payment-channel';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/payment-channel']);
    }

    goToPartnership() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'partnership';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.closeMobileSidebar();
        this.router.navigate(['/admin/partnership']);
    }

    toggleSettingDropdown() {
        this.isSettingDropdownOpen = !this.isSettingDropdownOpen;
    }

    closeSettingDropdown() {
        this.isSettingDropdownOpen = false;
    }

    toggleSidebar() {
        this.isSidebarMinimized = !this.isSidebarMinimized;
        localStorage.setItem(
            'isSidebarMinimized',
            String(this.isSidebarMinimized)
        );

        // Close submenu if minimized to prevent layout issues
        if (this.isSidebarMinimized) {
            this.isSubMenuOpen = false;
            localStorage.setItem('isSubMenuOpen', 'false');
            localStorage.setItem('isRolePermissionSubMenuOpen', 'false');
        }
    }

    toggleMobileSidebar() {
        this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    }

    closeMobileSidebar() {
        this.isMobileSidebarOpen = false;
    }

    toggleDropdown() {
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    @HostListener('document:click', ['$event'])
    closeDropdown(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target.closest('.relative')) {
            this.isDropdownOpen = false;
        }
    }

    goToProfile() {
        this.isSubMenuOpen = false;
        localStorage.setItem('isSubMenuOpen', 'false');
        this.activeMenu = 'profile';
        localStorage.setItem('activeMenu', this.activeMenu);
        this.router.navigate(['/admin/profile']);
    }

    async onLogout(): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Logout',
            text: 'Apakah Anda yakin ingin keluar?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Logout',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            this.isLoggingOut = true;
            this.logoutService.logoutAndRedirect();
        }
    }
}
